import {Button, Input, InputGroup, toaster, Toggle} from "rsuite";
import React, {useEffect, useRef, useState} from "react";
import {
  ReadSensorCoords_ReadSensorCoordsResponse,
  ReadSensorGpsTime_ReadSensorGpsTimeResponse,
  ReadSensorParams_ReadSensorParamsResponse,
} from "../../../../../api";
import {SensorBlockApi} from "../../../../../services/SensorBlock.api";
import NotificationFactory from "../../../../../shared/Notifications";
import UpdateButton from "../../../../shared/UpdateButton/UpdateButton";
import SensorBlockInfoSection from "./components/SensorBlockInfoSection";
import StatusControl from "../../../../shared/StatusControl/StatusControl";
import {extractTimezoneFromISO, gpsStatus, setStatus} from "../Dimmer/utils/Dimmer.utils";
import CopyIcon from "@rsuite/icons/Copy";
import ReactYandexMap from "../../../../shared/ReactYandexMap/ReactYandexMap";
import {TfiSaveIcon, VscEditIcon} from "../../../../../models/Icons.model";
import {SensorBlockProps} from "./SensorBlock.model";
import {SharedApi} from "../../../../../services/Shared.api";
import DeviceEvents from "../../../../shared/DeviceEvents/DeviceEvents";
import moment from "moment";
import {formatTimezoneOffset} from "../../../../../shared/Time.utils";
import DateTimeEditModal from "../../../../modals/DateTimeEditModal/DateTimeEditModal";
import RebootConfirmationModal from "../Dimmer/components/RebootConfirmationModal";

const SensorBlock = ({sensorId, isAdmin}: SensorBlockProps) => {
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState<ReadSensorParams_ReadSensorParamsResponse>({});
  const [gpsCoordinates, setGpsCoordinates] = useState<ReadSensorCoords_ReadSensorCoordsResponse | null>(null);
  const [gpsTime, setGpsTime] = useState<ReadSensorGpsTime_ReadSensorGpsTimeResponse>({});
  const [shockCounter, setShockCounter] = useState<number>(0);
  const [openCounter, setOpenCounter] = useState<number>(0);
  const [deviceTime, setDeviceTime] = useState<Date | null>(null);
  const [userTimezone, setUserTimezone] = useState("+3");
  const [currentUtcDateTime, setCurrentUtcDateTime] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [currentGpsTime, setCurrentGpsTime] = useState("");
  const [isDaylightSavingEnabled, setIsDaylightSavingEnabled] = useState(false);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [showRebootModal, setShowRebootModal] = useState(false);
  const timeMomentRef = useRef<moment.Moment | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const gpsTimeMomentRef = useRef<moment.Moment | null>(null);
  const gpsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    getParams().then();
    getGpsCoordinates().then();
    getGpsTime().then();
    getTime().then();
  }, [sensorId]);

  useEffect(() => {
    setShockCounter(params?.doorShockCounter || 0);
    setOpenCounter(params?.doorOpenCounter || 0);
  }, [params]);

  useEffect(() => {
    setCurrentUtcDateTime(getCurrentUtcDateTime());

    const utcIntervalId = setInterval(() => {
      setCurrentUtcDateTime(getCurrentUtcDateTime());
    }, 1000);

    return () => clearInterval(utcIntervalId);
  }, []);

  useEffect(() => {
    updateTimeFromDeviceTime();

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (timeMomentRef.current) {
        timeMomentRef.current.add(1, "seconds");
        setCurrentTime(timeMomentRef.current.format("DD.MM.YYYY HH:mm:ss"));
      }
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [deviceTime]);

  useEffect(() => {
    if (gpsIntervalRef.current) {
      clearInterval(gpsIntervalRef.current);
    }

    if (gpsTime?.time) {
      const m = moment(gpsTime.time);
      if (m.isValid()) {
        gpsTimeMomentRef.current = m;
        setCurrentGpsTime(m.format("DD.MM.YYYY HH:mm:ss"));

        gpsIntervalRef.current = setInterval(() => {
          if (gpsTimeMomentRef.current) {
            gpsTimeMomentRef.current.add(1, "seconds");
            setCurrentGpsTime(gpsTimeMomentRef.current.format("DD.MM.YYYY HH:mm:ss"));
          }
        }, 1000);
      } else {
        gpsTimeMomentRef.current = null;
        setCurrentGpsTime(gpsTime.time);
      }
    } else {
      gpsTimeMomentRef.current = null;
      setCurrentGpsTime("");
    }

    return () => {
      if (gpsIntervalRef.current) {
        clearInterval(gpsIntervalRef.current);
      }
    };
  }, [gpsTime]);

  const updateAll = () => {
    getParams().then();
    getGpsCoordinates().then();
    getGpsTime().then();
    getTime().then();
  }

  const getCurrentUtcDateTime = () => {
    return moment().utc().format("DD.MM.YYYY HH:mm:ss");
  };

  const getTimeMoment = (date: Date | string | moment.Moment | null) => {
    if (!date) return moment();

    try {
      if (typeof date === "string") {
        return moment(date, "DD.MM.YYYY HH:mm:ss");
      }

      if (date instanceof Date) {
        return moment(date);
      }

      if (moment.isMoment(date)) {
        return date.clone();
      }

      return moment();
    } catch (error) {
      console.error("Error creating moment:", error);
      return moment();
    }
  };

  const updateTimeFromDeviceTime = () => {
    if (deviceTime) {
      const m = getTimeMoment(deviceTime);
      timeMomentRef.current = m;
      setCurrentTime(m.format("DD.MM.YYYY HH:mm:ss"));
    } else {
      timeMomentRef.current = null;
      setCurrentTime("");
    }
  };

  const getParams = async () => {
    setLoading(true);
    try {
      const result = await SensorBlockApi.getSensorBlockParams(sensorId as string);
      setParams(result);
    } catch (error) {
      toaster.push(NotificationFactory.error("Ошибка"), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const getTime = async () => {
    setLoading(true);
    try {
      const data = await SharedApi.getTime(sensorId as string);
      setIsDaylightSavingEnabled(Boolean(data?.isDaylightSavingEnabled));

      if (data.time) {
        setUserTimezone(extractTimezoneFromISO(data.time));

        const match = data.time.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
        const parsedDate = match
          ? new Date(
            parseInt(match[1], 10),
            parseInt(match[2], 10) - 1,
            parseInt(match[3], 10),
            parseInt(match[4], 10),
            parseInt(match[5], 10),
            parseInt(match[6], 10)
          )
          : new Date(data.time);

        setDeviceTime(parsedDate);

        const m = getTimeMoment(parsedDate);
        timeMomentRef.current = m;
        setCurrentTime(m.format("DD.MM.YYYY HH:mm:ss"));
      } else {
        setDeviceTime(null);
      }
    } catch (error) {
      toaster.push(NotificationFactory.error("Ошибка"), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const updateDaylightSaving = async (enabled: boolean) => {
    setLoading(true);
    try {
      const result = await SharedApi.updateDaylightSaving(sensorId as string, enabled);

      if (result) {
        setIsDaylightSavingEnabled(enabled);
        toaster.push(
          NotificationFactory.success("Настройки сохранены"),
          {placement: "topEnd"}
        );
      } else {
        toaster.push(
          NotificationFactory.error("Не удалось обновить"),
          {placement: "topEnd"}
        );
      }
    } catch (error) {
      console.error("Ошибка при обновлении настроек:", error);
      toaster.push(
        NotificationFactory.error("Ошибка"),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDaylightSaving = async (enabled: boolean) => {
    await updateDaylightSaving(enabled);
  };

  const onSaveUTCTime = async (newDate: Date) => {
    setLoading(true);
    try {
      const ok = await SharedApi.updateUTCTime(sensorId as string, newDate.toISOString() as unknown as string);
      if (ok) {
        toaster.push(NotificationFactory.success("Настройки сохранены"), {placement: "topEnd"});
        await getTime();
      } else {
        toaster.push(NotificationFactory.error("Не удалось обновить"), {placement: "topEnd"});
      }
    } catch (error) {
      toaster.push(NotificationFactory.error("Ошибка"), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const onSaveTimezone = async () => {
    setLoading(true);
    try {
      const formattedTimezone = formatTimezoneOffset(userTimezone);
      const ok = await SharedApi.updateTimezone(sensorId as string, formattedTimezone);
      if (ok) {
        toaster.push(NotificationFactory.success("Настройки сохранены"), {placement: "topEnd"});
        await getTime();
      } else {
        toaster.push(NotificationFactory.error("Не удалось обновить"), {placement: "topEnd"});
      }
    } catch (error) {
      toaster.push(NotificationFactory.error("Ошибка"), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const handleTimezoneChange = (value: string) => {
    if (/^[+-]?\d*$/.test(value)) {
      setUserTimezone(value);
    }
  };

  const getGpsCoordinates = async () => {
    setLoading(true);
    try {
      const result = await SensorBlockApi.getSensorBlockGpsCoordinates(sensorId as string);
      setGpsCoordinates(result);
    } catch (error) {
      toaster.push(NotificationFactory.error("Ошибка"), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const getGpsTime = async () => {
    setLoading(true);
    try {
      const result = await SensorBlockApi.getSensorBlockGpsTime(sensorId as string);
      setGpsTime(result);
    } catch (error) {
      toaster.push(NotificationFactory.error("Ошибка"), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const updateCounters = async () => {
    setLoading(true);
    try {
      const ok = await SensorBlockApi.updateCounters(sensorId as string, {
        bump: +shockCounter,
        door: +openCounter
      });
      if (ok) {
        getParams().then();
        toaster.push(
          NotificationFactory.success("Счетчики обновлены"),
          {placement: "topEnd"}
        );
      } else {
        toaster.push(
          NotificationFactory.error("Не удалось обновить"),
          {placement: "topEnd"}
        );
      }
    } catch (error) {
      toaster.push(
        NotificationFactory.error("Ошибка при обновлении"),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(gpsCoordinates?.coordinates ?? "");
      toaster.push(
        NotificationFactory.success("Координаты скопированы в буфер обмена"),
        {placement: "topEnd"}
      );
    } catch (err) {
      console.error("Ошибка при копировании: ", err);
      toaster.push(
        NotificationFactory.error("Не удалось скопировать координаты"),
        {placement: "topEnd"}
      );
    }
  };

  const handleOpenRebootModal = () => {
    setShowRebootModal(true);
  };

  const handleCloseRebootModal = () => {
    setShowRebootModal(false);
  };

  const handleConfirmReboot = async () => {
    setShowRebootModal(false);
    setLoading(true);
    try {
      const result = await SharedApi.rebootDevice(String(sensorId));

      if (result) {
        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );
      } else {
        toaster.push(
          NotificationFactory.error(),
          {placement: "topEnd"}
        );
      }
    } catch (error) {
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
      console.error("Ошибка при перезапуске устройства:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fieldset-wrapper p-2">
        <div className="legend-title main-legend-title">Блок датчиков</div>
        <div className="row">
          <div className="col-md-12 text-end">
            <UpdateButton loading={loading} onRefresh={updateAll}/>
          </div>
        </div>
        <div className="row  pt-1 align-items-center">
          <div className="col-md-2">
            <InputGroup>
              <InputGroup.Addon>Влаж.</InputGroup.Addon>
              <Input value={params?.humidity ?? ""} style={{textAlign: "center"}}/>
            </InputGroup>
          </div>
          <div className="col-md-2">
            <InputGroup>
              <InputGroup.Addon>Давл.</InputGroup.Addon>
              <Input value={params?.pressure ?? ""} style={{textAlign: "center"}}/>
            </InputGroup>
          </div>
          <div className="col-md-3">
            <InputGroup>
              <InputGroup.Addon>Темп.</InputGroup.Addon>
              <Input value={params?.temperature ?? ""} style={{textAlign: "center"}}/>
            </InputGroup>
          </div>
          <div className="col-md-5">
            <InputGroup>
              <InputGroup.Addon>GPS время</InputGroup.Addon>
              <Input value={currentGpsTime || "--/--"} style={{textAlign: "center"}}/>
            </InputGroup>
          </div>
          {/*<div className="col-md-3">*/}
          {/*  <InputGroup>*/}
          {/*    <InputGroup.Addon>GPS час. пояс</InputGroup.Addon>*/}
          {/*    <Input value={gpsTime?.timeOffset ?? "--/--"} style={{ textAlign: "center" }} />*/}
          {/*  </InputGroup>*/}
          {/*</div>*/}
        </div>
        <div className="row mt-2 align-items-center">
          <div className="col-md-4">
            <InputGroup>
              <InputGroup.Addon>Время и дата</InputGroup.Addon>
              <Input readOnly value={currentTime} className="bg-green"/>
              <InputGroup.Addon
                onClick={() => setIsTimeModalOpen(true)}
                style={{cursor: "pointer"}}
                title="Редактировать">
                <VscEditIcon/>
              </InputGroup.Addon>
            </InputGroup>
          </div>
          <div className="col-md-3">
            <InputGroup>
              <InputGroup.Addon>Час. пояс</InputGroup.Addon>
              <Input
                value={userTimezone}
                onChange={handleTimezoneChange}
                style={{textAlign: "center"}}
              />
              <InputGroup.Addon>
                <TfiSaveIcon
                  style={{cursor: "pointer"}}
                  onClick={onSaveTimezone}
                />
              </InputGroup.Addon>
            </InputGroup>
          </div>
          <div className="col-md-5">
            <InputGroup>
              <InputGroup.Addon>Время и дата UTC</InputGroup.Addon>
              <Input
                className="bg-yellow"
                readOnly
                value={currentUtcDateTime}
              />
            </InputGroup>
          </div>
          <div className="col-md-12 mt-3">
            <Toggle
              size="sm"
              color="blue"
              checkedChildren="ON"
              unCheckedChildren="OFF"
              checked={isDaylightSavingEnabled}
              onChange={handleToggleDaylightSaving}
              disabled={loading}
              loading={loading}
            >
              Авт. переход зима / лето
            </Toggle>
          </div>
        </div>

        <div className="row mt-2 align-items-center">
          <div className="col-md-2">
            <InputGroup>
              <InputGroup.Addon>Удары</InputGroup.Addon>
              <Input
                value={shockCounter}
                onChange={(value) => setShockCounter(Number(value))}
                disabled={loading}
              />
              <InputGroup.Addon>
                <TfiSaveIcon
                  style={{cursor: "pointer"}}
                  onClick={updateCounters}
                />
              </InputGroup.Addon>
            </InputGroup>
          </div>
          <div className="col-md-2">
            <InputGroup>
              <InputGroup.Addon>Откр.</InputGroup.Addon>
              <Input
                value={openCounter}
                onChange={(value) => setOpenCounter(Number(value))}
                disabled={loading}
              />
              <InputGroup.Addon>
                <TfiSaveIcon
                  style={{cursor: "pointer"}}
                  onClick={updateCounters}
                />
              </InputGroup.Addon>
            </InputGroup>
          </div>
          <div className="col-md-2 text-center mt-1">
            <Toggle
              size="sm"
              color="blue"
              checkedChildren="ON"
              unCheckedChildren="OFF"
              checked={params?.doorReedSwitch}
              readOnly={true}
              loading={loading}
            >
              Дверь отк / зак
            </Toggle>
          </div>
          <div className="col-3">
            <div className="d-flex align-items-center">
              <div className="me-2">
                <div className="relay-indicator">
                  <StatusControl status={gpsCoordinates?.status === gpsStatus.none ? "offline" : "online"}/>
                </div>
              </div>
              <div className="relay-mode-value mt-0">
                {setStatus(gpsCoordinates?.status)}
              </div>
            </div>
          </div>
          <div className="col-3">
            <p style={{color: "#666"}}>
              {gpsCoordinates?.coordinates || "Нет данных о координатах"}
              <CopyIcon onClick={handleCopyClick} style={{cursor: "pointer"}} className="ms-2 mb-1"/>
            </p>
          </div>
        </div>
        <div className="row p-2">
          <div className="col-md-6 ps-1 pe-1">
            <SensorBlockInfoSection sensorId={sensorId} isAdmin={isAdmin}/>
              <div className="row mt-2">
                <div className="col-md-12 text-end">
                  <Button
                    color="red"
                    appearance="primary"
                    type="button"
                    onClick={handleOpenRebootModal}
                    loading={loading}
                    disabled={loading}
                  >
                    Перезапустить устройство
                  </Button>
                </div>

              </div>
          </div>
          <div className="col-md-6">
            <ReactYandexMap coordinates={gpsCoordinates?.coordinates ?? undefined}/>
          </div>
        </div>


        <div className="row">
          <div className="col-md-12">
            <DeviceEvents deviceId={sensorId}/>
          </div>
        </div>
      </div>
      <DateTimeEditModal
        open={isTimeModalOpen}
        onClose={() => setIsTimeModalOpen(false)}
        initialValue={deviceTime}
        loading={loading}
        title="Редактировать дату и время"
        onSave={async (newMoment) => {
          const newDate = newMoment.toDate();

          setDeviceTime(newDate);
          timeMomentRef.current = newMoment.clone();
          setCurrentTime(newMoment.format("DD.MM.YYYY HH:mm:ss"));

          await onSaveUTCTime(newDate);
          setIsTimeModalOpen(false);
        }}
      />
      <RebootConfirmationModal
        show={showRebootModal}
        onHide={handleCloseRebootModal}
        onConfirm={handleConfirmReboot}
      />
    </>
  );
};

export default SensorBlock;
